import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Logout, AccountCircle, ArrowBack } from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  Box,
  useTheme,
  Button,
  Typography,
} from "@mui/material";
import i18n from "i18next";
import { selectCurrentToken } from "../../features/slices/authSlice";
import { logout } from "../../features/slices/authSlice";
import * as Constant from "../../features/constant";
import { useGetProfileQuery } from "../../features/api/userApiSlice";

import Image from "../common/Image";
import DropdownMenu from "../common/DropdownMenu";
import Backdrop from "../common/Backdrop";

import { useTranslation } from "react-i18next";
import LanguageDropdown from "../common/LanguageDropdown";
import { useGetLanguagesQuery } from "../../features/api/publicApiSlice";
import NotificationBadgeIcon from "../notification/NotificationBadgeIcon";
import NotificationDropdown from "../notification/NotificationDropdown";
import useNotificationCount from "../../hooks/useNotificationCount";
import { lighten, darken, alpha } from "@material-ui/core";


const MobileHeader = ({ depositDetails }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector(selectCurrentToken);
  const [showPopup, setShowPopup] = useState(true);
  const signupLight = lighten(theme.palette.button.signup, 0.1);
  const signupDark = darken(theme.palette.button.signup, 0.05);
  const signupPulse = alpha(lighten(theme.palette.button.signup, 0.2), 0.4);
  const loginLight = lighten(theme.palette.button.login, 0.1);
  const loginDark = darken(theme.palette.button.login, 0.05);
  const location = useLocation();
  const logoutProfileItems = [
    {
      label: i18n.t("walletcard.logout"),
      icon: <Logout sx={{ height: 20, color: process.env.REACT_APP_COLOR_PRIMARY || "white" }} />,
      id: "logout-btn"
    },
  ];
  const [showHeader, setShowHeader] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    switch (location.pathname) {
      case Constant.getPath("/profileWallet"):
        setTitle("Profile")
        setShowHeader(false);
        break;

      case Constant.getPath("/deposit"):
        setTitle(t("walletcard.deposit"))
        setShowHeader(false);
        break;
      case Constant.getPath("/withdrawal"):
        setTitle(t("walletcard.withdrawal"))
        setShowHeader(false);
        break;
      case Constant.getPath("/promocenter"):
        setTitle(t("walletcard.promocenter"))
        setShowHeader(false);
        break;
      case Constant.getPath("/mission"):
        setTitle(t("mission.missiondiary"))
        setShowHeader(false);
        break;
      case Constant.getPath("/redemptioncode"):
        setTitle(t("walletcard.redemptioncode"))
        setShowHeader(false);
        break;
      case Constant.getPath("/transaction"):
        setTitle(t("walletcard.transaction"))
        setShowHeader(false);
        break;
      case Constant.getPath("/ongoingbonus"):
        setTitle(t("walletcard.ongoingbonus"))
        setShowHeader(false);
        break;

      case Constant.getPath("/betrecord"):
        setTitle(t("walletcard.betrecord"))
        setShowHeader(false);
        break;

      case Constant.getPath("/promorecord"):
        setTitle(t("walletcard.promotionrecord"))
        setShowHeader(false);
        break;

      case Constant.getPath("/myaccount"):
        setTitle(t("walletcard.myaccount"))
        setShowHeader(false);
        break;

      case Constant.getPath("/bankaccount"):
        setTitle(t("walletcard.bankAccount"))
        setShowHeader(false);
        break;

      case Constant.getPath("/changepassword"):
        setTitle(t("walletcard.changepassword"))
        setShowHeader(false);
        break;

      case Constant.getPath("/history"):
        setTitle(t("walletcard.history"))
        setShowHeader(false);
        break;

      case Constant.getPath("/messaging"):
        setTitle(t("messaging.title", "Messaging"))
        setShowHeader(false);
        break;

      default:
        setShowHeader(true);
    }
  }, [location.pathname]);

  const handleClose = () => {
    setShowPopup(false);
  };
  const handleButtonClick = () => {
    const landingUrl = process.env.REACT_APP_APK_LANDING_URL;
    if (landingUrl) {
      window.open(landingUrl, "_blank");
    } else {
      window.open(`${window.location.origin}/${process.env.REACT_APP_APK}`, "_blank");
    }
  };

  const {
    data: languages,
    isFetching: isLanguagesFetching,
    isLoading: isLanguagesLoading,
    isSuccess: isLanguagesSuccess,
  } = useGetLanguagesQuery({ prefetch: true });

  const {
    data: user,
    isFetching: isUserFetching,
    isLoading: isUserLoading,
  } = useGetProfileQuery({ prefetch: true });

  // Notification state (same behaviour as DesktopHeader's mail icon)
  const { counts: unreadCounts, totalUnread } = useNotificationCount();
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const notifOpen = Boolean(notifAnchorEl);
  const handleNotifClick = (event) => {
    setNotifAnchorEl(notifAnchorEl ? null : event.currentTarget);
  };
  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  if (isUserFetching || isUserLoading) {
    return <Backdrop />;
  }

  const handleLogoutOnClick = (item) => {
    if (item?.label === i18n.t("walletcard.logout")) {
      dispatch(logout());
      navigate(Constant.getPath("/"));
    }
  };

  /*fancy gradient headers*/
  const gradient1 = process.env.REACT_APP_COLOR_HEADER_GRADIENT1?.trim();
  const gradient2 = process.env.REACT_APP_COLOR_HEADER_GRADIENT2?.trim();
  const headerImage = process.env.REACT_APP_MOBILE_HEADER_IMAGE?.trim();
  let headerColor;
  if (gradient1 && gradient2) {
    headerColor = `linear-gradient(to right, ${gradient1}, ${gradient2})`;
  } else if (headerImage) {
    headerColor = `url(${headerImage})`;
  } else {
    headerColor = theme.palette.header;
  }

  /*deposit and withdrawal button borders*/
  const buttonBorderColor = process.env.REACT_APP_COLOR_HEADER_DEPOWITHDRAW_BUTTONBORDER;
  /*fancy gradient deposit and withdrawal buttons*/
  const gradientButton = (index) => {
    const deposit1 = process.env.REACT_APP_COLOR_HEADER_DEPOSIT_GRADIENT1;
    const deposit2 = process.env.REACT_APP_COLOR_HEADER_DEPOSIT_GRADIENT2;
    const depositImg = process.env.REACT_APP_IMG_DEPOSIT;

    if (deposit1 && deposit2) {
      return `linear-gradient(to right, ${deposit1}, ${deposit2})`;
    }

    if (depositImg) {
      return `url(${depositImg})`;
    }

    return theme.palette.button.secondary.main;
  };

  return (
    <>
      {showHeader ? (
        <AppBar position="fixed" sx={{ marginBottom: '10px' }}>
          <Toolbar
            sx={{
              background: headerColor,
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: 0,
            }}
          >
            {/* Flex row: the right side never shrinks, the logo shrinks to the
                remaining space, so the two can never overlap. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  maxHeight: 50,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={process.env.REACT_APP_MOBILE_HEADER_LOGO}
                  onClick={() => navigate(Constant.getPath("/"))}
                  alt={Constant.ALT_TAG_LOGO}
                  style={{
                    cursor: "pointer",
                    maxWidth: "min(86px, 100%)",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                  }}
                />
              </Box>
              {token && (
                <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
                  <Button
                    variant="contained"
                    sx={{
                      minWidth: "54px",
                      height: "26px",
                      borderRadius: '3px', // Add border-radius for curved corners
                      background: gradientButton(),
                      border: buttonBorderColor ? `1px solid ${buttonBorderColor}` : "none",
                      padding: 0,
                      fontSize: "10px",
                      backgroundSize: "100% 100%",
                    }}
                    disableRipple
                    onClick={() =>
                      navigate(Constant.getPath("/deposit"), {
                        state: {
                          depositDetails: depositDetails,
                        },
                      })
                    }
                  >
                    {t("walletcard.deposit")}
                  </Button>
                  <NotificationBadgeIcon
                    totalUnread={totalUnread}
                    onClick={handleNotifClick}
                    sx={{ p: 0.5, mx: 0.25, fontSize: 22 }}
                  />
                  <NotificationDropdown
                    anchorEl={notifAnchorEl}
                    open={notifOpen}
                    onClose={handleNotifClose}
                    counts={unreadCounts}
                  />
                  <Typography sx={{
                    color: process.env.REACT_APP_COLOR_ICON || "white",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    mx: 0.5,
                  }}>
                    {user?.result?.currentCredit}
                  </Typography>
                  <DropdownMenu
                    sxMenu={{
                      mt: "40px",
                    }}
                    items={logoutProfileItems}
                    handleOnClickEvent={handleLogoutOnClick}
                    icon={true}
                  >
                    <AccountCircle sx={{ color: process.env.REACT_APP_COLOR_ICON || 'white' }} />
                  </DropdownMenu>

                </Box>
              )}
              {/* Mobile view login & register (below banner) */}
              {!token && (
                <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
                    {/* Promotion icon is dropped on very narrow screens so the logo keeps room */}
                    <Box
                      sx={{
                        mr: "10px",
                        cursor: "pointer",
                        "@media (max-width:359px)": { display: "none" },
                      }}
                      onClick={() => {
                        navigate(Constant.getPath("/promotion"));
                      }}
                    >
                      <Image
                        width={25}
                        height={25}
                        src={process.env.REACT_APP_HEADER_LOGO + "/PROMOTION.png"}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundImage: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
                        // backgroundImage: 'url(/assets/img/yellow_big.png)',
                        minWidth: "64px",
                        height: "30px",
                        marginRight: "8px", // Add margin to create a gap
                        borderRadius: '3px', // Add border-radius for curved corners
                        padding: 0,
                        px: 1.5,
                        fontSize: "12px",
                        backgroundSize: "100% 100%",

                      }}
                      disableRipple
                      // onClick={() =>
                      //   navigate(Constant.getPath("/login"))
                      // }
                      onClick={() =>
                        navigate({
                          pathname: location.pathname,
                          search: "?login=1",
                        })
                      }
                    >
                      {t("home.login")}
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        minWidth: "64px",
                        height: "30px",
                        borderRadius: '3px', // Add border-radius for curved corners
                        backgroundImage: `linear-gradient(to bottom, ${signupLight} 50%, ${signupDark} 50%)`,
                        // backgroundImage: 'url(/assets/img/red_big.png)',
                        padding: 0,
                        px: 1.5,
                        fontSize: "12px",
                        backgroundSize: "100% 100%",
                        transition: 'box-shadow 0.4s ease-in-out',
                        '&:hover': {
                          boxShadow: `0 0 20px ${signupPulse}`, // Adjust hover shadow color and size as needed
                          backgroundImage: `linear-gradient(to bottom, ${signupLight} 50%, ${signupDark} 50%)`,
                        },
                        '@keyframes shadow-pulse': {
                          '0%': {
                            boxShadow: `0 0 0 5px ${signupPulse}`, // Adjust initial shadow color and size as needed
                          },
                          '80%': {
                            boxShadow: `0 0 5px ${signupPulse}`, // Adjust pulse shadow color and size as needed
                          },
                        },
                        '@keyframes vibration': {
                          '0%': {
                            transform: 'translateX(0)',
                          },
                          '25%': {
                            transform: 'translateX(-2px)',
                          },
                          '50%': {
                            transform: 'translateX(2px)',
                          },
                          '75%': {
                            transform: 'translateX(-2px)',
                          },
                          '100%': {
                            transform: 'translateX(0)',
                          },
                        },
                        animation: 'shadow-pulse 1s infinite alternate, vibration 0.7s infinite', // Combine pulse and vibration animations

                      }}
                      disableRipple
                      onClick={() =>
                        // navigate(Constant.getPath("/register"))
                        navigate({
                          pathname: location.pathname,
                          search: "?register=1",
                        })
                      }
                    >
                      {t("home.signup")}
                    </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar >
      ) : (
        <AppBar position="fixed" sx={{
          background: headerColor,
          boxShadow: "0px 6px 15px  rgba(0, 0, 0, 0.5)",
        }}>
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
              height: 56, // or whatever height you prefer
            }}
          >
            {/* Back Arrow */}
            <Box
              onClick={() => window.history.back()}
              sx={{
                position: "absolute",
                left: 16,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ArrowBack sx={{ color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || process.env.REACT_APP_COLOR_TEXT }} />
            </Box>

            {/* Title in the middle */}
            <Typography
              variant="h6"
              sx={{
                color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
                textAlign: "center",
                width: "100%",
                fontWeight: "bold",
              }}
            >
              {title}
            </Typography>
          </Toolbar>
        </AppBar >
      )}
    </>
  );
};

MobileHeader.propTypes = {
  background: PropTypes.string,
};

MobileHeader.defaultProps = {
  background: "",
};

export default MobileHeader;